import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, Calendar, Loader2, BarChart3, Receipt, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function GSTExport() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');

  const loadSummary = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/gst/summary', { params });
      setSummary(res.data);
    } catch { toast.error('Failed to load GST summary'); }
    setLoading(false);
  };

  useEffect(() => { loadSummary(); }, [dateFrom, dateTo]);

  const exportFile = async (type) => {
    setExporting(type);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const endpoint = type === 'gstr1' ? '/gst/gstr1-export' : '/gst/gstr3b-export';
      const res = await api.get(`${endpoint}?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `${type.toUpperCase()}_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
      a.click();
      toast.success(`${type.toUpperCase()} exported successfully`);
    } catch { toast.error('Export failed'); }
    setExporting('');
  };

  return (
    <div className="space-y-5" data-testid="gst-export-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">GST Return Export</h1>
          <p className="text-sm text-slate-500">Export GST data in filing-ready format</p>
        </div>
        <div className="flex gap-3">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="form-input w-36" data-testid="gst-date-from" />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="form-input w-36" data-testid="gst-date-to" />
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white border rounded-lg p-4 text-center">
            <Receipt size={18} className="mx-auto mb-2 text-orange-500" />
            <p className="text-2xl font-bold text-slate-800">{summary.total_invoices}</p>
            <p className="text-xs text-slate-500">Total Invoices</p>
          </div>
          <div className="bg-white border rounded-lg p-4 text-center">
            <BarChart3 size={18} className="mx-auto mb-2 text-blue-500" />
            <p className="text-lg font-bold text-slate-800">{summary.total_taxable_value?.toLocaleString('en-IN')}</p>
            <p className="text-xs text-slate-500">Taxable Value</p>
          </div>
          <div className="bg-white border rounded-lg p-4 text-center">
            <p className="text-lg font-bold text-emerald-600">{summary.total_gst_collected?.toLocaleString('en-IN')}</p>
            <p className="text-xs text-slate-500">GST Collected</p>
          </div>
          <div className="bg-white border rounded-lg p-4 text-center">
            <p className="text-lg font-bold text-slate-800">{summary.b2b_invoices}</p>
            <p className="text-xs text-slate-500">B2B Invoices</p>
          </div>
          <div className="bg-white border rounded-lg p-4 text-center">
            <p className="text-lg font-bold text-slate-800">{summary.b2c_invoices}</p>
            <p className="text-xs text-slate-500">B2C Invoices</p>
          </div>
        </div>
      )}

      {/* Export Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileSpreadsheet size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800">GSTR-1 Export</h3>
              <p className="text-xs text-slate-500">Outward supplies — B2B, B2C, HSN Summary</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
            Exports all outward supply data including B2B invoices (with recipient GSTIN),
            B2C small invoices, and HSN-wise summary. Compatible with the GST portal upload format.
          </p>
          <ul className="text-xs text-slate-500 space-y-1 mb-4">
            <li className="flex items-center gap-1.5"><ArrowRight size={10} className="text-blue-500" />B2B Invoice details with GSTIN</li>
            <li className="flex items-center gap-1.5"><ArrowRight size={10} className="text-blue-500" />B2C aggregated summary</li>
            <li className="flex items-center gap-1.5"><ArrowRight size={10} className="text-blue-500" />HSN/SAC wise summary</li>
          </ul>
          <Button onClick={() => exportFile('gstr1')} disabled={!!exporting} className="w-full" data-testid="export-gstr1">
            {exporting === 'gstr1' ? <><Loader2 size={14} className="animate-spin mr-1" /> Exporting...</> : <><Download size={14} className="mr-1" /> Export GSTR-1 (CSV)</>}
          </Button>
        </div>

        <div className="bg-white border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <FileSpreadsheet size={20} className="text-emerald-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800">GSTR-3B Export</h3>
              <p className="text-xs text-slate-500">Monthly return summary — Outward, ITC, Net Tax</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
            Generates a GSTR-3B compatible summary with outward supply tax breakdown,
            eligible Input Tax Credit (ITC), and net tax payable calculations.
          </p>
          <ul className="text-xs text-slate-500 space-y-1 mb-4">
            <li className="flex items-center gap-1.5"><ArrowRight size={10} className="text-emerald-500" />3.1 — Outward supplies (IGST/CGST/SGST)</li>
            <li className="flex items-center gap-1.5"><ArrowRight size={10} className="text-emerald-500" />4 — Eligible ITC summary</li>
            <li className="flex items-center gap-1.5"><ArrowRight size={10} className="text-emerald-500" />5 — Net tax payable</li>
          </ul>
          <Button onClick={() => exportFile('gstr3b')} disabled={!!exporting} variant="outline" className="w-full" data-testid="export-gstr3b">
            {exporting === 'gstr3b' ? <><Loader2 size={14} className="animate-spin mr-1" /> Exporting...</> : <><Download size={14} className="mr-1" /> Export GSTR-3B (CSV)</>}
          </Button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <p className="text-xs text-amber-800">
          <strong>Note:</strong> Exported CSV files are compatible with the GST portal bulk upload format.
          Review the data before filing. Tax calculations are based on invoices and purchase entries in the system.
        </p>
      </div>
    </div>
  );
}
